import { Component, inject, input, output, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ButtonDirective } from 'primeng/button';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'ems-file-upload',
  standalone: true,
  imports: [ButtonDirective],
  templateUrl: './file-upload.html',
  styleUrls: ['./file-upload.scss'],
})
export class FileUploadComponent {
  apiBaseUrl = input('file');
  userId = input<number>();
  bucketConfig = input.required<string>();
  category = input.required<string>();

  uploaded = output<number>();
  selectedFile = signal<File | null>(null);
  uploading = signal(false);
  error = signal<string | null>(null);
  dragActive = signal(false);
  validExtensions = input.required<string[]>();

  private http = inject(HttpClient);
  #messageService: MessageService = inject(MessageService);

  onFileSelected(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.setFile(inputElement.files?.[0] ?? null);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragActive.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragActive.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragActive.set(false);
    this.setFile(event.dataTransfer?.files?.[0] ?? null);
  }

  private setFile(file: File | null): void {
    if (file) {
      const dotIndex = file.name.lastIndexOf('.');
      const extension = dotIndex >= 0 ? file.name.substring(dotIndex).toLowerCase() : '';

      if (!this.validExtensions().includes(extension)) {
        this.#messageService.add({
          severity: 'error',
          summary: 'Could not import file',
          detail: `Extension type ${extension} is not allowed here`,
        });
        return;
      }
    }

    this.selectedFile.set(file);
    this.error.set(null);
  }

  upload(): void {
    const file = this.selectedFile();
    if (!file) {
      return;
    }

    const formData = new FormData();
    if (this.userId()) {
      formData.append('userId', String(this.userId()));
    }
    formData.append('file', file);
    formData.append('category', this.category());
    formData.append('bucketConfig', this.bucketConfig());

    this.uploading.set(true);
    this.error.set(null);

    // Don't set a Content-Type header here - the browser needs to add the
    // multipart boundary itself, which it won't do if one is set manually.
    this.http.post<number>(this.apiBaseUrl(), formData).subscribe({
      next: (response) => {
        this.uploading.set(false);
        this.selectedFile.set(null);
        this.uploaded.emit(response);
      },
      error: (err: HttpErrorResponse) => {
        this.error.set(err.error?.message ?? err.message ?? 'Upload failed');
        this.uploading.set(false);
      },
    });
  }
}
